import React, { useState } from 'react'
import Navbar from '../src/components/Navbar'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts'

// Color mapping for issue severity
const SEVERITY_COLORS = {
  high: '#EF4444',    // red-500
  medium: '#F59E0B',  // amber-500
  low: '#3B82F6'      // blue-500
};

// Grade colors
const GRADE_COLORS = {
  'A+': '#10B981', 'A': '#10B981', 'A-': '#10B981',  // green
  'B+': '#3B82F6', 'B': '#3B82F6', 'B-': '#3B82F6',  // blue
  'C+': '#F59E0B', 'C': '#F59E0B', 'C-': '#F59E0B',  // amber
  'D+': '#F97316', 'D': '#F97316', 'D-': '#F97316',  // orange
  'F': '#6B7280'  // gray
};

// Hard-coded data for screenshots
const MOCK_ORGS = [
  { id: 'org-1', name: 'XYZ Corp', instance_url: 'https://xyz.my.salesforce.com', is_sandbox: false },
  { id: 'org-2', name: 'XYZ Corp', instance_url: 'https://xyz--sandbox.my.salesforce.com', is_sandbox: true }
];

const MOCK_METRICS = {
  healthGrade: 'B+',
  averageScore: 78,
  criticalIssues: 12,
  incompleteFlows: 8,
  weeklyFixed: 23,
  weeklyTrend: 'up' as const,
  totalFlows: 124,
  issuesByType: {
    'Missing Error Handling': 45,
    'No Description': 38,
    'Performance Issues': 29,
    'DML in Loops': 15,
    'Complex Logic': 22
  },
  lastSync: new Date().toLocaleString()
};

const MOCK_BURN_UP_DATA = {
  chartData: [
    { date: 'May 22', cumulativeFixed: 0 },
    { date: 'May 29', cumulativeFixed: 8 },
    { date: 'Jun 5', cumulativeFixed: 19 },
    { date: 'Jun 12', cumulativeFixed: 35 },
    { date: 'Jun 13', cumulativeFixed: 48 },
    { date: 'Jun 14', cumulativeFixed: 71 },
    { date: 'Jun 15', cumulativeFixed: 82 },
    { date: 'Jun 16', cumulativeFixed: 95 },
    { date: 'Jun 17', cumulativeFixed: 106 },
    { date: 'Jun 18', cumulativeFixed: 112 },
    { date: 'Jun 19', cumulativeFixed: 114 }
  ],
  summary: {
    thisWeek: 32,
    thisMonth: 66,
    allTime: 114
  }
};

const MOCK_ISSUE_DISTRIBUTION = [
  { type: 'missing_error_handling', displayName: 'Missing Error Handling', count: 45, severity: 'high' },
  { type: 'no_description', displayName: 'No Description', count: 38, severity: 'medium' },
  { type: 'performance_issues', displayName: 'Performance Issues', count: 29, severity: 'high' },
  { type: 'complex_logic', displayName: 'Complex Logic', count: 22, severity: 'medium' },
  { type: 'dml_in_loops', displayName: 'DML in Loops', count: 15, severity: 'high' },
  { type: 'hardcoded_ids', displayName: 'Hardcoded IDs', count: 12, severity: 'medium' },
  { type: 'missing_null_checks', displayName: 'Missing Null Checks', count: 9, severity: 'low' },
  { type: 'no_bulkification', displayName: 'Not Bulkified', count: 8, severity: 'high' }
];

const MOCK_QUICK_WINS = [
  {
    flowId: 'flow-1',
    flowName: 'Lead Assignment Flow',
    title: 'Add Error Handling',
    description: 'Add try-catch blocks to handle potential exceptions',
    estimatedMinutes: 15,
    points: 5
  },
  {
    flowId: 'flow-2',
    flowName: 'Opportunity Validation',
    title: 'Add Flow Description',
    description: 'Document the flow purpose and business logic',
    estimatedMinutes: 5,
    points: 2
  },
  {
    flowId: 'flow-3',
    flowName: 'Case Escalation Process',
    title: 'Remove DML in Loop',
    description: 'Move Update Records element outside of the loop',
    estimatedMinutes: 30,
    points: 8
  },
  {
    flowId: 'flow-4',
    flowName: 'Account Sync Flow',
    title: 'Add Null Checks',
    description: 'Add decision elements to check for null values',
    estimatedMinutes: 20,
    points: 5
  },
  {
    flowId: 'flow-5',
    flowName: 'Contact Deduplication',
    title: 'Optimize SOQL Queries',
    description: 'Combine multiple Get Records into a single query',
    estimatedMinutes: 25,
    points: 7
  }
];

const MOCK_FLOW_ERROR_DATA = {
  chartData: [
    { date: 'Jun 6', count: 156 },
    { date: 'Jun 7', count: 143 },
    { date: 'Jun 8', count: 129 },
    { date: 'Jun 9', count: 135 },
    { date: 'Jun 10', count: 108 },
    { date: 'Jun 11', count: 95 },
    { date: 'Jun 12', count: 102 },
    { date: 'Jun 13', count: 145 },
    { date: 'Jun 14', count: 132 },
    { date: 'Jun 15', count: 118 },
    { date: 'Jun 16', count: 125 },
    { date: 'Jun 17', count: 98 },
    { date: 'Jun 18', count: 87 },
    { date: 'Jun 19', count: 92 }
  ],
  summary: {
    total: 1665,
    lastWeek: 868,  // Jun 6-12
    thisWeek: 797,  // Jun 13-19
    avgPerDay: 119,
    trend: 'decreasing' as const  // 868 -> 797
  }
};

const MOCK_ERROR_DISTRIBUTION = [
  { type: 'FLOW_RUNTIME_ERROR', displayName: 'Runtime Error', count: 234, category: 'Runtime', severity: 'high' },
  { type: 'REQUIRED_FIELD_MISSING', displayName: 'Required Field Missing', count: 189, category: 'Validation', severity: 'medium' },
  { type: 'FIELD_INTEGRITY_EXCEPTION', displayName: 'Field Integrity Exception', count: 156, category: 'Data', severity: 'high' },
  { type: 'DUPLICATE_VALUE', displayName: 'Duplicate Value', count: 98, category: 'Data', severity: 'medium' },
  { type: 'LIMIT_EXCEEDED', displayName: 'Governor Limit Exceeded', count: 67, category: 'Limits', severity: 'high' },
  { type: 'INSUFFICIENT_ACCESS', displayName: 'Insufficient Access', count: 53, category: 'Security', severity: 'medium' }
];

const MOCK_AFFECTED_USERS = [
  {
    userId: 'user-1',
    displayName: 'Sarah Johnson',
    errorCount: 145,
    uniqueFlowCount: 12,
    mostProblematicFlow: { name: 'Lead Assignment Flow', errors: 67 },
    lastError: '10 minutes ago'
  },
  {
    userId: 'user-2',
    displayName: 'Mike Chen',
    errorCount: 98,
    uniqueFlowCount: 8,
    mostProblematicFlow: { name: 'Case Escalation Process', errors: 45 },
    lastError: '2 hours ago'
  },
  {
    userId: 'user-3',
    displayName: 'Emily Davis',
    errorCount: 76,
    uniqueFlowCount: 5,
    mostProblematicFlow: { name: 'Opportunity Validation', errors: 38 },
    lastError: '1 hour ago'
  },
  {
    userId: 'user-4',
    displayName: 'James Wilson',
    errorCount: 54,
    uniqueFlowCount: 6,
    mostProblematicFlow: { name: 'Account Sync Flow', errors: 29 },
    lastError: '3 hours ago'
  },
  {
    userId: 'user-5',
    displayName: 'Lisa Martinez',
    errorCount: 43,
    uniqueFlowCount: 4,
    mostProblematicFlow: { name: 'Contact Deduplication', errors: 22 },
    lastError: '45 minutes ago'
  }
];

// Mock data for flows affected by error types
const MOCK_ERROR_AFFECTED_FLOWS = [
  {
    errorType: 'Runtime Error',
    displayName: 'Runtime Error',
    totalErrors: 234,
    flows: [
      { flowId: 'flow-1', flowName: 'Lead Assignment Flow', errorCount: 89 },
      { flowId: 'flow-3', flowName: 'Case Escalation Process', errorCount: 67 },
      { flowId: 'flow-5', flowName: 'Contact Deduplication', errorCount: 45 },
      { flowId: 'flow-7', flowName: 'Order Processing', errorCount: 33 }
    ]
  },
  {
    errorType: 'Required Field Missing',
    displayName: 'Required Field Missing',
    totalErrors: 189,
    flows: [
      { flowId: 'flow-2', flowName: 'Opportunity Validation', errorCount: 78 },
      { flowId: 'flow-4', flowName: 'Account Sync Flow', errorCount: 56 },
      { flowId: 'flow-6', flowName: 'Quote Generation', errorCount: 34 },
      { flowId: 'flow-8', flowName: 'Invoice Creation', errorCount: 21 }
    ]
  },
  {
    errorType: 'Field Integrity Exception',
    displayName: 'Field Integrity Exception',
    totalErrors: 156,
    flows: [
      { flowId: 'flow-1', flowName: 'Lead Assignment Flow', errorCount: 67 },
      { flowId: 'flow-9', flowName: 'Campaign Member Update', errorCount: 45 },
      { flowId: 'flow-10', flowName: 'Task Assignment', errorCount: 44 }
    ]
  },
  {
    errorType: 'Duplicate Value',
    displayName: 'Duplicate Value',
    totalErrors: 98,
    flows: [
      { flowId: 'flow-5', flowName: 'Contact Deduplication', errorCount: 45 },
      { flowId: 'flow-11', flowName: 'Lead Conversion', errorCount: 32 },
      { flowId: 'flow-12', flowName: 'Account Merge', errorCount: 21 }
    ]
  },
  {
    errorType: 'Governor Limit Exceeded',
    displayName: 'Governor Limit Exceeded',
    totalErrors: 67,
    flows: [
      { flowId: 'flow-13', flowName: 'Bulk Data Update', errorCount: 34 },
      { flowId: 'flow-14', flowName: 'Mass Email Send', errorCount: 23 },
      { flowId: 'flow-15', flowName: 'Territory Assignment', errorCount: 10 }
    ]
  },
  {
    errorType: 'Insufficient Access',
    displayName: 'Insufficient Access',
    totalErrors: 53,
    flows: [
      { flowId: 'flow-16', flowName: 'Executive Dashboard Update', errorCount: 28 },
      { flowId: 'flow-17', flowName: 'Restricted Record Access', errorCount: 25 }
    ]
  }
];

export default function Dashboard() {
  const [selectedOrgId, setSelectedOrgId] = useState<string>('org-1')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [quickWinsExpanded, setQuickWinsExpanded] = useState(false)
  const [affectedUsersExpanded, setAffectedUsersExpanded] = useState(false)
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set())
  const [errorFlowsExpanded, setErrorFlowsExpanded] = useState(false)
  const [expandedErrorTypes, setExpandedErrorTypes] = useState<Set<string>>(new Set())
  const [connectionStatus] = useState<{type: 'success' | 'error' | 'none', message: string | React.ReactNode}>({
    type: 'success',
    message: 'Last sync completed successfully'
  })

  const toggleGroup = (issueType: string) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(issueType)) {
      newExpanded.delete(issueType)
    } else {
      newExpanded.add(issueType)
    }
    setExpandedGroups(newExpanded)
  }

  // Group quick wins by issue type
  const groupedQuickWins = MOCK_QUICK_WINS.reduce((acc, win) => {
    if (!acc[win.title]) {
      acc[win.title] = []
    }
    acc[win.title].push(win)
    return acc
  }, {} as Record<string, typeof MOCK_QUICK_WINS>)

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with org selector on same line */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Flow Health Dashboard</h1>
            
            {/* Org selector on the right */}
            <div className="flex items-center space-x-3">
              <label htmlFor="org-select" className="text-sm font-medium text-gray-700">
                Select Organization:
              </label>
              <select
                id="org-select"
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                className="pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {MOCK_ORGS.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({org.is_sandbox ? 'Sandbox' : 'Production'})
                  </option>
                ))}
              </select>
              
              {/* Refresh button */}
              <button
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Main Dashboard Content */}
        <main>
          {/* Connection Status Alert */}
          {connectionStatus.type !== 'none' && (
            <div className={`rounded-md p-4 mb-6 ${
              connectionStatus.type === 'success' ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {connectionStatus.type === 'success' ? (
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">
                    {connectionStatus.message}
                  </p>
                </div>
              </div>
            </div>
          )}
          

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Health Grade */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Health Grade</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: GRADE_COLORS[MOCK_METRICS.healthGrade as keyof typeof GRADE_COLORS] }}>
                    {MOCK_METRICS.healthGrade}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full flex items-center justify-center" 
                     style={{ backgroundColor: `${GRADE_COLORS[MOCK_METRICS.healthGrade as keyof typeof GRADE_COLORS]}20` }}>
                  <span className="text-2xl">📊</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Ave. Score {MOCK_METRICS.averageScore}% over {MOCK_METRICS.totalFlows} flows
              </p>
            </div>

            {/* Critical Issues */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Critical Issues</p>
                  <p className="text-3xl font-bold text-red-600 mt-2">{MOCK_METRICS.criticalIssues}</p>
                </div>
                <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">⚠️</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Requires immediate attention</p>
            </div>

            {/* Incomplete Flows */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Incomplete Flows</p>
                  <p className="text-3xl font-bold text-amber-600 mt-2">{MOCK_METRICS.incompleteFlows}</p>
                </div>
                <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">📝</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Missing analysis</p>
            </div>

            {/* Impacted Users */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Impacted Users</p>
                  <p className="text-3xl font-bold text-purple-600 mt-2">{MOCK_AFFECTED_USERS.length}</p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">👥</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">With flow error interviews</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Burn-up Chart - COMMENTED OUT FOR FUTURE USE
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Issues Fixed Over Time</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={MOCK_BURN_UP_DATA.chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="cumulativeFixed" 
                      stroke="#10B981" 
                      strokeWidth={2}
                      dot={{ fill: '#10B981' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-sm text-gray-500">This Week</p>
                  <p className="text-xl font-semibold text-gray-900">{MOCK_BURN_UP_DATA.summary.thisWeek}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">This Month</p>
                  <p className="text-xl font-semibold text-gray-900">{MOCK_BURN_UP_DATA.summary.thisMonth}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">All Time</p>
                  <p className="text-xl font-semibold text-gray-900">{MOCK_BURN_UP_DATA.summary.allTime}</p>
                </div>
              </div>
            </div>
            */}

            {/* Issues Over Time - Burn Down Chart */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Issues Over Time</h2>
              {(() => {
                // Create realistic burn down data with more aggressive reduction to show gradient
                const burnDownData = [
                  { date: 'Jun 5', totalIssues: 94, newIssues: 0, fixedIssues: 0 },
                  { date: 'Jun 6', totalIssues: 89, newIssues: 1, fixedIssues: 6 },
                  { date: 'Jun 7', totalIssues: 82, newIssues: 0, fixedIssues: 7 },
                  { date: 'Jun 8', totalIssues: 74, newIssues: 1, fixedIssues: 9 },
                  { date: 'Jun 9', totalIssues: 68, newIssues: 2, fixedIssues: 8 },
                  { date: 'Jun 10', totalIssues: 61, newIssues: 0, fixedIssues: 7 },
                  { date: 'Jun 11', totalIssues: 54, newIssues: 1, fixedIssues: 8 },
                  { date: 'Jun 12', totalIssues: 48, newIssues: 0, fixedIssues: 6 },
                  { date: 'Jun 13', totalIssues: 43, newIssues: 1, fixedIssues: 6 },
                  { date: 'Jun 14', totalIssues: 58, newIssues: 18, fixedIssues: 3 }, // Spike - new flow deployed
                  { date: 'Jun 15', totalIssues: 51, newIssues: 0, fixedIssues: 7 },
                  { date: 'Jun 16', totalIssues: 44, newIssues: 1, fixedIssues: 8 },
                  { date: 'Jun 17', totalIssues: 38, newIssues: 0, fixedIssues: 6 },
                  { date: 'Jun 18', totalIssues: 32, newIssues: 0, fixedIssues: 6 },
                  { date: 'Jun 19', totalIssues: 27, newIssues: 0, fixedIssues: 5 }
                ];

                const startIssues = burnDownData[0].totalIssues;
                const currentIssues = burnDownData[burnDownData.length - 1].totalIssues;
                const totalFixed = startIssues - currentIssues + burnDownData.reduce((sum, d) => sum + d.newIssues, 0);
                const totalNew = burnDownData.reduce((sum, d) => sum + d.newIssues, 0);

                const formatDate = (date: string) => {
                  const [month, day] = date.split(' ');
                  return `${month} ${day}`;
                };

                return (
                  <div>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={burnDownData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={formatDate}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip 
                            content={({ active, payload, label }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 border border-gray-200 rounded shadow-sm">
                                    <p className="font-medium text-sm">{formatDate(label)}</p>
                                    <p className="text-sm text-gray-600">Total Issues: {data.totalIssues}</p>
                                    {data.newIssues > 0 && (
                                      <p className="text-sm text-red-600">New Issues: +{data.newIssues}</p>
                                    )}
                                    {data.fixedIssues > 0 && (
                                      <p className="text-sm text-green-600">Fixed: -{data.fixedIssues}</p>
                                    )}
                                    {data.flowsModified && data.flowsModified.length > 0 && (
                                      <div className="mt-2 pt-2 border-t border-gray-100">
                                        <p className="text-xs font-medium text-gray-700">Flows Modified:</p>
                                        {data.flowsModified.slice(0, 3).map((flow: string, idx: number) => (
                                          <p key={idx} className="text-xs text-gray-600">• {flow}</p>
                                        ))}
                                        {data.flowsModified.length > 3 && (
                                          <p className="text-xs text-gray-500">+{data.flowsModified.length - 3} more</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <defs>
                            <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#EF4444" />
                              <stop offset="25%" stopColor="#EF4444" />
                              <stop offset="50%" stopColor="#F59E0B" />
                              <stop offset="75%" stopColor="#F59E0B" />
                              <stop offset="100%" stopColor="#10B981" />
                            </linearGradient>
                          </defs>
                          <Line 
                            type="monotone" 
                            dataKey="totalIssues" 
                            stroke="url(#lineGradient)" 
                            strokeWidth={2}
                            dot={(props: any) => {
                              const { cx, cy, payload } = props;
                              // Calculate color based on Y-axis position (not data range)
                              const yAxisMax = 100; // Approximate Y-axis max for this data
                              const percentage = 1 - (payload.totalIssues / yAxisMax);
                              
                              let color = '#EF4444'; // red
                              if (percentage > 0.67) {
                                color = '#10B981'; // green
                              } else if (percentage > 0.33) {
                                color = '#F59E0B'; // amber
                              }
                              
                              // Highlight spike points
                              if (payload.newIssues > 10) {
                                return (
                                  <circle cx={cx} cy={cy} r={6} fill={color} stroke="#fff" strokeWidth={2} />
                                );
                              }
                              return <circle cx={cx} cy={cy} r={4} fill={color} />;
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-xs text-gray-500">Current Issues</p>
                        <p className="text-lg font-semibold text-red-600">
                          {currentIssues}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Issues Fixed</p>
                        <p className="text-lg font-semibold text-green-600">
                          {totalFixed}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">New Issues</p>
                        <p className="text-lg font-semibold text-amber-600">
                          {totalNew}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Issue Distribution */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Issue Distribution</h2>
              <div className="space-y-3">
                {MOCK_ISSUE_DISTRIBUTION.map((issue, index) => (
                  <div key={index} className="flex items-center">
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-sm text-gray-700 truncate pr-4" style={{ minWidth: '200px' }}>
                        {issue.displayName}
                      </span>
                      <div className="flex items-center flex-1">
                        <div className="flex-1 bg-gray-200 rounded-full h-6 mr-3 relative overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-300"
                            style={{ 
                              width: `${(issue.count / Math.max(...MOCK_ISSUE_DISTRIBUTION.map(i => i.count))) * 100}%`,
                              backgroundColor: SEVERITY_COLORS[issue.severity as keyof typeof SEVERITY_COLORS]
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-12 text-right">
                          {issue.count}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-center space-x-6">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                  <span className="text-sm text-gray-600">High Severity</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                  <span className="text-sm text-gray-600">Medium Severity</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  <span className="text-sm text-gray-600">Low Severity</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Wins Section */}
          <div className="mt-8 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Quick Wins - Top Improvements</h3>
                  <p className="text-sm text-gray-500">Easy fixes that can improve your flow health quickly</p>
                </div>
                <div className="flex items-center space-x-4">
                  <p className="text-sm text-gray-600">
                    Total: {MOCK_QUICK_WINS.length} quick wins across {Object.keys(groupedQuickWins).length} issues • 
                    {' '}{MOCK_QUICK_WINS.reduce((sum, w) => sum + w.points, 0)} points • 
                    {' '}~{Math.floor(MOCK_QUICK_WINS.reduce((sum, w) => sum + w.estimatedMinutes, 0) / 60)}h {MOCK_QUICK_WINS.reduce((sum, w) => sum + w.estimatedMinutes, 0) % 60}m
                  </p>
                  <button
                    onClick={() => setQuickWinsExpanded(!quickWinsExpanded)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className={`w-5 h-5 transform transition-transform ${quickWinsExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            {quickWinsExpanded && (
              <div className="p-6">
                <div className="space-y-4">
                  {Object.entries(groupedQuickWins).slice(0, 5).map(([issueType, flows]) => {
                const totalPoints = flows.reduce((sum, f) => sum + f.points, 0)
                const totalTime = flows.reduce((sum, f) => sum + f.estimatedMinutes, 0)
                const isExpanded = expandedGroups.has(issueType)
                
                return (
                  <div key={issueType} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-4 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h4 className="text-sm font-medium text-gray-900">{issueType}</h4>
                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                              {totalPoints} points
                            </span>
                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                              {flows.length} flows
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{flows[0].description}</p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <p className="text-sm text-gray-600">
                            ~{Math.floor(totalTime / 60) > 0 ? `${Math.floor(totalTime / 60)}h ` : ''}{totalTime % 60}m
                          </p>
                          <button
                            onClick={() => toggleGroup(issueType)}
                            className="ml-4 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
                          >
                            <span>View Flows</span>
                          <svg 
                            className={`ml-2 h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="border-t border-gray-200 bg-white">
                        <div className="divide-y divide-gray-200">
                          {flows.map((flow) => (
                            <div key={flow.flowId} className="px-4 py-3 hover:bg-gray-50 flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{flow.flowName}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {flow.estimatedMinutes} min • {flow.points} points
                                </p>
                              </div>
                              <a
                                href={`#flow-${flow.flowId}`}
                                className="ml-4 text-sm text-blue-600 hover:text-blue-800"
                              >
                                View Flow →
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )})}
                </div>
              </div>
            )}
          </div>

          {/* Flow Errors Section */}
          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Error Trend Chart */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Flow Errors - Last 14 Days</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={MOCK_FLOW_ERROR_DATA.chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#EF4444" 
                      strokeWidth={2}
                      dot={{ fill: '#EF4444' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 grid grid-cols-5 gap-2">
                <div className="text-center">
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-lg font-semibold text-gray-900">{MOCK_FLOW_ERROR_DATA.summary.total}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Last Week</p>
                  <p className="text-lg font-semibold text-gray-900">{MOCK_FLOW_ERROR_DATA.summary.lastWeek}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">This Week</p>
                  <p className="text-lg font-semibold text-gray-900">{MOCK_FLOW_ERROR_DATA.summary.thisWeek}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Avg/Day</p>
                  <p className="text-lg font-semibold text-gray-900">{MOCK_FLOW_ERROR_DATA.summary.avgPerDay}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Trend</p>
                  <p className={`text-lg font-semibold ${
                    MOCK_FLOW_ERROR_DATA.summary.trend === 'decreasing' ? 'text-green-600' : 
                    MOCK_FLOW_ERROR_DATA.summary.trend === 'increasing' ? 'text-red-600' : 
                    'text-gray-600'
                  }`}>
                    {(() => {
                      const percentChange = Math.round((MOCK_FLOW_ERROR_DATA.summary.thisWeek - MOCK_FLOW_ERROR_DATA.summary.lastWeek) / MOCK_FLOW_ERROR_DATA.summary.lastWeek * 100);
                      const arrow = MOCK_FLOW_ERROR_DATA.summary.trend === 'decreasing' ? '↓' : 
                                   MOCK_FLOW_ERROR_DATA.summary.trend === 'increasing' ? '↑' : '→';
                      return `${arrow} ${Math.abs(percentChange)}%`;
                    })()}
                  </p>
                </div>
              </div>
            </div>

            {/* Error Type Distribution */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Error Types</h2>
              <div className="space-y-3">
                {MOCK_ERROR_DISTRIBUTION.slice(0, 5).map((error) => (
                  <div key={error.type} className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className={`w-2 h-2 rounded-full mr-2 ${
                        error.severity === 'high' ? 'bg-red-500' :
                        error.severity === 'medium' ? 'bg-amber-500' :
                        'bg-blue-500'
                      }`} />
                      <span className="text-sm text-gray-900 truncate pr-2">{error.displayName}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-600">{error.count}</span>
                  </div>
                ))}
                {MOCK_ERROR_DISTRIBUTION.length > 5 && (
                  <p className="text-xs text-gray-500 text-center pt-2">
                    +{MOCK_ERROR_DISTRIBUTION.length - 5} more error types
                  </p>
                )}
              </div>
              {MOCK_ERROR_DISTRIBUTION.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                  <button
                    onClick={() => {
                      setErrorFlowsExpanded(true);
                      // Scroll to the error flows section
                      setTimeout(() => {
                        document.getElementById('error-flows-section')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    View Related Flows
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Affected Users */}
          <div className="mt-8 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Most Affected Users</h3>
                  <p className="text-sm text-gray-500">Users experiencing the most flow errors</p>
                </div>
                <button
                  onClick={() => setAffectedUsersExpanded(!affectedUsersExpanded)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className={`w-5 h-5 transform transition-transform ${affectedUsersExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
            {affectedUsersExpanded && (
              <div className="p-6">
                <div className="space-y-4">
                  {MOCK_AFFECTED_USERS.slice(0, 5).map((user, index) => {
                    const isExpanded = expandedUsers.has(user.userId);
                    return (
                      <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="p-4 bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <h4 className="text-sm font-medium text-gray-900">{user.displayName}</h4>
                                <span className="ml-2 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                                  {user.errorCount} errors
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                  {user.uniqueFlowCount} flows
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Last error: {user.lastError} • Most problematic: {user.mostProblematicFlow?.name || 'Unknown'}
                              </p>
                            </div>
                            <button
                              onClick={() => {
                                const newExpanded = new Set(expandedUsers);
                                if (isExpanded) {
                                  newExpanded.delete(user.userId);
                                } else {
                                  newExpanded.add(user.userId);
                                }
                                setExpandedUsers(newExpanded);
                              }}
                              className="ml-4 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
                            >
                              <span>View Flows</span>
                              <svg 
                                className={`ml-2 h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-gray-200 bg-white">
                            {user.mostProblematicFlow ? (
                              <div className="divide-y divide-gray-200">
                                <div className="px-4 py-3 hover:bg-gray-50 flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{user.mostProblematicFlow.name}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {user.mostProblematicFlow.errors} errors
                                    </p>
                                  </div>
                                  <a
                                    href="#"
                                    className="ml-4 text-sm text-blue-600 hover:text-blue-800"
                                  >
                                    View Flow →
                                  </a>
                                </div>
                                {/* Additional flows would be listed here */}
                              </div>
                            ) : (
                              <p className="px-4 py-3 text-sm text-gray-500">
                                No flow error details available
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {MOCK_AFFECTED_USERS.length > 5 && (
                    <p className="text-xs text-gray-500 text-center pt-2">
                      +{MOCK_AFFECTED_USERS.length - 5} more users affected
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Error-Related Flows Section */}
          <div id="error-flows-section" className="mt-8 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Flows by Error Type</h3>
                  <p className="text-sm text-gray-500">See which flows are experiencing each type of error</p>
                </div>
                <button
                  onClick={() => setErrorFlowsExpanded(!errorFlowsExpanded)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className={`w-5 h-5 transform transition-transform ${errorFlowsExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
            {errorFlowsExpanded && (
              <div className="p-6">
                <div className="space-y-4">
                  {MOCK_ERROR_DISTRIBUTION.map((errorType) => {
                    const isExpanded = expandedErrorTypes.has(errorType.type);
                    return (
                      <div key={errorType.type} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="p-4 bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <h4 className="text-sm font-medium text-gray-900">{errorType.displayName}</h4>
                                <span className="ml-2 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                                  {errorType.count} errors
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const newExpanded = new Set(expandedErrorTypes);
                                if (isExpanded) {
                                  newExpanded.delete(errorType.type);
                                } else {
                                  newExpanded.add(errorType.type);
                                }
                                setExpandedErrorTypes(newExpanded);
                              }}
                              className="ml-4 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
                            >
                              <span>View Flows</span>
                              <svg 
                                className={`ml-2 h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-gray-200 bg-white">
                            <div className="px-4 py-3">
                              {/* Find the matching error group from our mock data */}
                              {(() => {
                                const errorGroup = MOCK_ERROR_AFFECTED_FLOWS.find(g => g.displayName === errorType.displayName);
                                if (errorGroup && errorGroup.flows) {
                                  return (
                                    <div className="divide-y divide-gray-200">
                                      {errorGroup.flows.map((flow) => (
                                        <div key={flow.flowId} className="py-3 hover:bg-gray-50 flex items-center justify-between">
                                          <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">{flow.flowName}</p>
                                            <p className="text-xs text-gray-500 mt-1">
                                              {flow.errorCount} errors
                                            </p>
                                          </div>
                                          <a
                                            href={`#flow-${flow.flowId}`}
                                            className="ml-4 text-sm text-blue-600 hover:text-blue-800"
                                          >
                                            View Flow →
                                          </a>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                }
                                return (
                                  <p className="text-sm text-gray-500 text-center">
                                    Loading flow data...
                                  </p>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}